import LocalStorage from 'Components/Helpers/LocalStorage';

const LAST_CONVERSATION_KEY = 'assistant:lastConversationId';
const LAST_SEEN_KEY = 'assistant:lastConversationSeenAt';

/** How long a chat stays "current" after you look away from it (WEB-10278).
 *  Come back inside the window and the workspace / floating chat reopens that
 *  conversation; after it you land on a fresh chat and the "Continue
 *  conversation" link above the composer is the manual way back. */
export const RESUME_WINDOW_MS = 10 * 60 * 1000;

// Scoped per account and user, so switching accounts never resumes another account's
// conversation. Fresh per call to pick up a switch; null while logged out (no shared key).
const scopedStorage = (): LocalStorage | null => {
	const storage = new LocalStorage();
	const client = storage.AccountSelf?.client;
	return client?.id && client.userId ? storage : null;
};

const readLastConversationId = (storage: LocalStorage): number | null => {
	try {
		const val = storage.getValueRaw(LAST_CONVERSATION_KEY);
		return val ? Number(val) : null;
	} catch {
		return null;
	}
};

export const getLastConversationId = (): number | null => {
	const storage = scopedStorage();
	return storage ? readLastConversationId(storage) : null;
};

export const setLastConversationId = (id: number | null): void => {
	const storage = scopedStorage();
	if (!storage) return;
	try {
		if (id) {
			storage.setValueRaw(LAST_CONVERSATION_KEY, String(id));
			storage.setValueRaw(LAST_SEEN_KEY, String(Date.now()));
		} else {
			storage.removeValueRaw(LAST_CONVERSATION_KEY);
			storage.removeValueRaw(LAST_SEEN_KEY);
		}
	} catch {
		/* localStorage unavailable */
	}
};

/** Restarts the resume window on the conversation that is already stored.
 *  Called while a chat is on screen (tab hidden, surface unmounted) so the
 *  window measures time since you last looked at it, not since you opened it. */
export const touchLastConversation = (): void => {
	const storage = scopedStorage();
	if (!storage) return;
	try {
		if (!storage.getValueRaw(LAST_CONVERSATION_KEY)) return;
		storage.setValueRaw(LAST_SEEN_KEY, String(Date.now()));
	} catch {
		/* localStorage unavailable */
	}
};

/** The conversation to drop back into, or null to start a new chat. */
export const getResumableConversationId = (): number | null => {
	const storage = scopedStorage();
	const id = storage && readLastConversationId(storage);
	if (!storage || !id) return null;
	try {
		const seenAt = Number(storage.getValueRaw(LAST_SEEN_KEY));
		if (!seenAt || Date.now() - seenAt > RESUME_WINDOW_MS) return null;
	} catch {
		return null;
	}
	return id;
};
